import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface BottomSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showCloseButton?: boolean;
  maxHeight?: string;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
}

const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showCloseButton = true,
  maxHeight = 'max-h-[85vh]',
  className = '',
  contentClassName = '',
  headerClassName = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const hasMoved = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
    isDragging.current = false; // Don't start dragging immediately
    hasMoved.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    // Require minimum movement threshold (10px) to start dragging
    if (Math.abs(diff) > 10 && !isDragging.current) {
      isDragging.current = true;
      hasMoved.current = true;
      
      // Disable transition during drag for smooth movement
      if (modalRef.current) {
        modalRef.current.style.transition = 'none';
      }
    }
    
    // Allow dragging both up and down once drag has started
    if (isDragging.current && modalRef.current) {
      e.preventDefault(); // Prevent scrolling while dragging
      
      // Allow dragging down freely, but limit dragging up (negative diff)
      // Prevent dragging up beyond original position (max -50px for slight overscroll effect)
      const constrainedDiff = diff < 0 ? Math.max(diff, -50) : diff;
      modalRef.current.style.transform = `translateY(${constrainedDiff}px)`;
    }
  };

  const handleTouchEnd = () => {
    const diff = currentY.current - startY.current;
    
    // If no significant movement, treat as a click (do nothing)
    if (!hasMoved.current || !isDragging.current) {
      isDragging.current = false;
      hasMoved.current = false;
      return;
    }
    
    isDragging.current = false;
    hasMoved.current = false;
    
    // Re-enable transition for smooth snap back
    if (modalRef.current) {
      modalRef.current.style.transition = 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
    }
    
    // Close if dragged down more than 100px
    if (diff > 100) {
      // Animate out before closing
      if (modalRef.current) {
        modalRef.current.style.transform = 'translateY(100%)';
      }
      setTimeout(() => {
        onClose();
      }, 200);
    } else {
      // Snap back to original position
      if (modalRef.current) {
        modalRef.current.style.transform = 'translateY(0)';
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-md animate-fadeIn"
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div
        ref={modalRef}
        className={`relative w-full bg-white rounded-t-3xl shadow-2xl animate-slideUp transition-transform duration-300 ease-out ${maxHeight} ${className}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        {(title || showCloseButton) && (
          <div className={`flex items-center justify-between px-6 pb-4 ${headerClassName}`}>
            {title && (
              <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 active:scale-95"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className={`overflow-y-auto px-6 pb-6 ${contentClassName}`} style={{ maxHeight: 'calc(85vh - 100px)' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheetModal;
